'use client'

import React, { useEffect, useRef, useMemo, useState } from 'react'
import { IChartApi, ISeriesApi, UTCTimestamp, LineStyle, LineSeries } from 'lightweight-charts'
import type { 
  HeadAndShouldersPattern, 
  PatternPoint, 
  Point,
  PATTERN_COLORS
} from '../../types/drawing'
import {
  validateHeadAndShoulders,
  calculateProjectedTarget 
} from '../../types/drawing'
import PatternStatus from '../PatternStatus'
import { createVerticalLineData } from '../../utils/chartDataUtils'

interface HeadAndShouldersProps {
  chart: IChartApi
  pattern: HeadAndShouldersPattern
  onUpdate?: (pattern: HeadAndShouldersPattern) => void
  onDelete?: (patternId: string) => void
}

const HeadAndShoulders: React.FC<HeadAndShouldersProps> = ({
  chart,
  pattern,
  onUpdate,
  onDelete
}) => {
  // Series refs for pattern lines
  const necklineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null)
  const leftShoulderLineRef = useRef<ISeriesApi<'Line'> | null>(null)
  const headLineRef = useRef<ISeriesApi<'Line'> | null>(null)
  const rightShoulderLineRef = useRef<ISeriesApi<'Line'> | null>(null)
  const projectionLineRef = useRef<ISeriesApi<'Line'> | null>(null)

  // Validation state
  const [isValidating, setIsValidating] = useState(false)
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean
    confidence: number
    errors: string[]
  } | undefined>(undefined)
  const [showStatus, setShowStatus] = useState(true)

  // Pattern color based on type
  const patternColor = pattern.isInverted ? '#4CAF50' : '#F23645' // Green for inverse H&S, Red for normal H&S

  // Validate pattern points and update validation state
  const isValidPattern = useMemo(() => {
    try {
      const isValid = validateHeadAndShoulders(
        pattern.patternPoints.leftShoulder,
        pattern.patternPoints.head,
        pattern.patternPoints.rightShoulder,
        0.1, // 10% tolerance
        pattern.isInverted
      )
      
      // Calculate confidence based on pattern quality
      let confidence = 0.5
      let errors: string[] = []
      
      if (isValid) {
        // Check shoulder symmetry
        const leftShoulderPrice = pattern.patternPoints.leftShoulder.price
        const rightShoulderPrice = pattern.patternPoints.rightShoulder.price
        const headPrice = pattern.patternPoints.head.price
        
        const shoulderDiff = Math.abs(leftShoulderPrice - rightShoulderPrice)
        const avgShoulderPrice = (leftShoulderPrice + rightShoulderPrice) / 2
        const shoulderSymmetry = 1 - (shoulderDiff / avgShoulderPrice)
        
        // Check head prominence
        const leftHeadDiff = Math.abs(headPrice - leftShoulderPrice)
        const rightHeadDiff = Math.abs(headPrice - rightShoulderPrice)
        const avgHeadDiff = (leftHeadDiff + rightHeadDiff) / 2
        const headProminence = avgHeadDiff / avgShoulderPrice
        
        // Calculate overall confidence
        confidence = Math.min(0.95, 0.5 + shoulderSymmetry * 0.3 + Math.min(headProminence, 0.15))
        
        if (shoulderSymmetry < 0.8) {
          errors.push('Shoulders are not symmetrical')
        }
        if (headProminence < 0.05) {
          errors.push('Head is not prominent enough')
        }
      } else {
        errors.push('Invalid head and shoulders formation')
        confidence = 0.2
      }
      
      setValidationResult({
        isValid,
        confidence,
        errors
      })
      
      return isValid
    } catch (error) {
      setValidationResult({
        isValid: false,
        confidence: 0.1,
        errors: ['Pattern validation failed']
      })
      return false
    }
  }, [pattern.patternPoints])

  // Run validation when pattern changes
  useEffect(() => {
    setIsValidating(true)
    
    // Simulate validation delay for better UX
    const timer = setTimeout(() => {
      setIsValidating(false)
    }, 300)
    
    return () => clearTimeout(timer)
  }, [pattern.patternPoints])

  // Calculate projected target
  const projectedTarget = useMemo(() => {
    if (!pattern.confirmation || !isValidPattern) return null
    
    const necklinePrice = (pattern.patternPoints.neckline[0].price + pattern.patternPoints.neckline[1].price) / 2
    const headPrice = pattern.patternPoints.head.price
    const patternHeight = Math.abs(headPrice - necklinePrice)
    
    return calculateProjectedTarget(
      'head-and-shoulders',
      patternHeight,
      necklinePrice,
      !pattern.isInverted // Normal H&S is bearish reversal
    )
  }, [pattern, isValidPattern])

  // Initialize pattern drawing
  useEffect(() => {
    if (!chart || !isValidPattern) return

    // Create neckline
    necklineSeriesRef.current = chart.addSeries(LineSeries, {
      color: patternColor,
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
      title: `H&S Neckline`,
      priceLineVisible: false,
      lastValueVisible: false
    })

    // Create shoulder and head marker lines (vertical lines to mark peaks)
    leftShoulderLineRef.current = chart.addSeries(LineSeries, {
      color: patternColor,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      title: 'Left Shoulder',
      priceLineVisible: false,
      lastValueVisible: false
    })

    headLineRef.current = chart.addSeries(LineSeries, {
      color: patternColor,
      lineWidth: 2,
      lineStyle: LineStyle.Dashed,
      title: 'Head',
      priceLineVisible: false,
      lastValueVisible: false
    })

    rightShoulderLineRef.current = chart.addSeries(LineSeries, {
      color: patternColor,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      title: 'Right Shoulder',
      priceLineVisible: false,
      lastValueVisible: false
    })

    // Create projection line if pattern is confirmed
    if (pattern.confirmation && projectedTarget) {
      projectionLineRef.current = chart.addSeries(LineSeries, {
        color: '#FFD700', // Gold color for target
        lineWidth: 2,
        lineStyle: LineStyle.Dotted,
        title: `Target: ${projectedTarget.toFixed(2)}`,
        priceLineVisible: true,
        lastValueVisible: true
      })
    }

    return () => {
      // Cleanup on unmount
      if (necklineSeriesRef.current) {
        chart.removeSeries(necklineSeriesRef.current)
        necklineSeriesRef.current = null
      }
      if (leftShoulderLineRef.current) {
        chart.removeSeries(leftShoulderLineRef.current)
        leftShoulderLineRef.current = null
      }
      if (headLineRef.current) {
        chart.removeSeries(headLineRef.current)
        headLineRef.current = null
      }
      if (rightShoulderLineRef.current) {
        chart.removeSeries(rightShoulderLineRef.current)
        rightShoulderLineRef.current = null
      }
      if (projectionLineRef.current) {
        chart.removeSeries(projectionLineRef.current)
        projectionLineRef.current = null
      }
    }
  }, [chart, isValidPattern, patternColor, pattern.confirmation, projectedTarget])

  // Update neckline data
  useEffect(() => {
    if (!necklineSeriesRef.current || !pattern.patternPoints.neckline) return

    const necklineData = [
      {
        time: pattern.patternPoints.neckline[0].time as UTCTimestamp,
        value: pattern.patternPoints.neckline[0].price
      },
      {
        time: pattern.patternPoints.neckline[1].time as UTCTimestamp,
        value: pattern.patternPoints.neckline[1].price
      }
    ]

    necklineSeriesRef.current.setData(necklineData)
  }, [pattern.patternPoints.neckline])

  // Update shoulder and head markers
  useEffect(() => {
    // Left Shoulder marker - create vertical line using utility function
    if (leftShoulderLineRef.current) {
      const leftShoulderData = createVerticalLineData(
        pattern.patternPoints.leftShoulder.time,
        pattern.patternPoints.leftValley.price,
        pattern.patternPoints.leftShoulder.price
      )
      leftShoulderLineRef.current.setData(leftShoulderData)
    }

    // Head marker - create vertical line using utility function
    if (headLineRef.current) {
      const headData = createVerticalLineData(
        pattern.patternPoints.head.time,
        Math.min(pattern.patternPoints.leftValley.price, pattern.patternPoints.rightValley.price),
        pattern.patternPoints.head.price
      )
      headLineRef.current.setData(headData)
    }

    // Right Shoulder marker - create vertical line using utility function
    if (rightShoulderLineRef.current) {
      const rightShoulderData = createVerticalLineData(
        pattern.patternPoints.rightShoulder.time,
        pattern.patternPoints.rightValley.price,
        pattern.patternPoints.rightShoulder.price
      )
      rightShoulderLineRef.current.setData(rightShoulderData)
    }
  }, [pattern.patternPoints])

  // Update projection line
  useEffect(() => {
    if (!projectionLineRef.current || !projectedTarget || !pattern.confirmation) return

    const projectionData = [
      {
        time: pattern.patternPoints.neckline[1].time as UTCTimestamp,
        value: projectedTarget
      },
      {
        time: (pattern.patternPoints.neckline[1].time + 86400 * 30) as UTCTimestamp, // 30 days ahead
        value: projectedTarget
      }
    ]

    projectionLineRef.current.setData(projectionData)
  }, [projectedTarget, pattern.confirmation, pattern.patternPoints.neckline])

  // Pattern info display (could be expanded to show in a tooltip)
  const patternInfo = useMemo(() => {
    const quality = isValidPattern ? 'Valid' : 'Invalid'
    const type = pattern.isInverted ? 'Inverse H&S (Bullish)' : 'Head & Shoulders (Bearish)'
    const confirmation = pattern.confirmation ? 'Confirmed' : 'Pending'
    
    return {
      type,
      quality,
      confirmation,
      target: projectedTarget?.toFixed(2) || 'N/A'
    }
  }, [isValidPattern, pattern.isInverted, pattern.confirmation, projectedTarget])

  // Render pattern status overlay
  return (
    <>
      {showStatus && (
        <PatternStatus
          pattern={pattern}
          isValidating={isValidating}
          validationResult={validationResult}
          onClose={() => setShowStatus(false)}
        />
      )}
    </>
  )
}

export default HeadAndShoulders

// Helper function to create a new Head and Shoulders pattern
export const createHeadAndShouldersPattern = (
  id: string,
  points: PatternPoint[],
  isInverted: boolean = false
): HeadAndShouldersPattern => {
  if (points.length < 5) {
    throw new Error('Head and Shoulders pattern requires at least 5 points (left shoulder, left valley, head, right valley, right shoulder)')
  }

  const [leftShoulder, leftValley, head, rightValley, rightShoulder] = points

  // Create neckline connecting the two valleys
  const neckline: [PatternPoint, PatternPoint] = [
    { ...leftValley, role: 'neckline', significance: 0.8 },
    { ...rightValley, role: 'neckline', significance: 0.8 }
  ]

  return {
    id,
    type: 'head-and-shoulders',
    points: points.map(p => ({ time: p.time, price: p.price })),
    style: {
      lineColor: isInverted ? '#4CAF50' : '#F23645',
      lineWidth: 2,
      lineStyle: 'solid',
      textColor: '#E0E3EB',
      fontSize: 12
    },
    isSelected: false,
    isVisible: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    zIndex: 10,
    patternPoints: {
      leftShoulder: { ...leftShoulder, role: 'peak', significance: 0.7 },
      head: { ...head, role: 'peak', significance: 1.0 },
      rightShoulder: { ...rightShoulder, role: 'peak', significance: 0.7 },
      leftValley: { ...leftValley, role: 'valley', significance: 0.8 },
      rightValley: { ...rightValley, role: 'valley', significance: 0.8 },
      neckline
    },
    isInverted,
    confirmation: false,
    volume: undefined
  }
} 